import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import type { SavedLesson } from '@/mocks/lessons';

interface SummaryNotesProps {
  lesson: SavedLesson;
}

export default function SummaryNotes({ lesson }: SummaryNotesProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const t = {
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textMuted: isDark ? 'text-white/60' : 'text-black/60',
    textFaint: isDark ? 'text-white/25' : 'text-black/25',
    iconBg: isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]',
    iconMuted: isDark ? 'text-white/50' : 'text-black/40',
    heading: isDark ? 'text-white' : 'text-neutral-900',
    bullet: isDark ? 'text-white/15' : 'text-black/15',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
      className="px-5 pb-4"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className={`w-8 h-8 rounded-lg ${t.iconBg} flex items-center justify-center`}>
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-file-list-line ${t.iconMuted} text-sm`} />
          </div>
        </div>
        <div>
          <h3 className={`${t.textPrimary} text-sm font-semibold`}>Summary Notes</h3>
          <p className={`${t.textFaint} text-[11px]`}>{lesson.summary.title}</p>
        </div>
      </div>

      <div className="space-y-5">
        {lesson.summary.sections.map((section, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08, duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
          >
            <h4 className={`${t.heading} text-xs font-bold uppercase tracking-wider mb-3`}>
              {section.heading}
            </h4>
            <div className="space-y-2">
              {section.bullets.map((bullet, j) => (
                <motion.div
                  key={j}
                  initial={{ opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08 + j * 0.04 + 0.1, duration: 0.3 }}
                  className="flex items-start gap-2.5"
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`ri-checkbox-blank-circle-fill ${t.bullet} text-[6px]`} />
                  </div>
                  <p className={`${t.textMuted} text-xs leading-relaxed`}>{bullet}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}